import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "../../../components/Box";
import { Flex } from "../../../components/Flex";
import { Paragraph } from "../../../components/Paragraph";
import { H3 } from "../../../components/Heading";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { Card } from "../../../components/Card";
import { Loader } from "../../../components/Loader";
import Select, { Option } from "../../../components/Select";
import { ROUTE } from "../../../routes";
import { NewProfileFrame } from "../NewProfileFrame";
import { useGetProfileList } from "../../../data/useGetProfileList";
import theme from "../../../theme";
import { DatePicker } from "../../../components/DatePicker";
import { DateTime } from "luxon";
import { useCommonTitles } from "../../../data/useCommonTitles";
import { MIN_AGE_APPLICATION } from "../../../utils/constants";
import { Form, Formik } from "formik";
import { getAddIndividualValidation } from "../../../utils/validationSchema";
import { useMembershipDivisionTeam } from "../../../hooks/useMembershipDivisionTeam";
import { useAuth } from "../../../context/AuthContext";

type Mode = "search" | "selectOrCreate" | "addNew";

type IndividualFormState = {
  division: number | string;
  team: number | string;
  title: string | null;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: Date | null;
  email: string;
  yourReference: string;
  gender: string;
};

export const NewPersonProfile = () => {
  const [mode, setMode] = useState<Mode>("search");

  const [personSearch, setPersonSearch] = useState("");
  const [dateOfBirthSearch, setDateOfBirthSearch] = useState<Date | null>(null);

  const [submittedSearch, setSubmittedSearch] = useState("");
  const [submittedDobSearch, setSubmittedDobSearch] = useState<Date | null>(
    null
  );

  const [selectedIndividualId, setSelectedIndividualId] = useState<
    number | null
  >(null);

  const {
    divisionOptions,
    showDivisionField,
    singleDivisionId,
    teamOptionsFor,
    singleTeamId,
  } = useMembershipDivisionTeam();
  const { hasPermission } = useAuth();

  const [form, setForm] = useState<IndividualFormState>({
    division: singleDivisionId,
    team: singleTeamId,
    title: null,
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: null,
    email: "",
    yourReference: "",
    gender: "",
  });

  const genderOptions = [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
    { value: "other", label: "Other" },
  ];

  const { data: titlesData } = useCommonTitles();

  const titleOptions: Option[] =
    titlesData?.data?.map(({ name }: any) => ({
      label: name,
      value: name,
    })) ?? [];

  const navigate = useNavigate();

  const toYMD = (d?: Date | null) =>
    d instanceof Date && !isNaN(d.getTime())
      ? DateTime.fromJSDate(d).toFormat("yyyy-MM-dd")
      : "";

  const { data, isLoading } = useGetProfileList({
    type: "individual",
    search: submittedSearch || undefined,
    dateOfBirth: submittedDobSearch ? toYMD(submittedDobSearch) : undefined,
  });

  const results = data?.data?.results ?? [];

  const handleSearch = () => {
    setSubmittedSearch(personSearch.trim());
    setSubmittedDobSearch(dateOfBirthSearch);
    setSelectedIndividualId(null);
    setMode("selectOrCreate");
  };

  const handleNextWithExisting = () => {
    if (!selectedIndividualId) return;

    const existingIndividual = results.find(
      (person: any) => person.id === selectedIndividualId
    );

    navigate(ROUTE.PROFILE_NEW_PERSON_SUMMARY, {
      state: {
        existingIndividualId: selectedIndividualId,
        existingIndividual,
      },
    });
  };

  const handleNextWithNewIndividual = (values: IndividualFormState) => {
    setForm(values);

    navigate(ROUTE.PROFILE_NEW_PERSON_ADDRESS, {
      state: {
        newIndividual: {
          division: values.division,
          team: values.team,
          title: values.title,
          gender: values.gender,
          first_name: values.firstName.trim(),
          middle_name: values.middleName.trim(),
          last_name: values.lastName.trim(),
          date_of_birth: toYMD(values.dateOfBirth),
          email: values.email.trim(),
          yourReference: values.yourReference.trim(),
        },
      },
    });
  };

  const renderSearch = () => (
    <Box mb={3} minWidth={500}>
      <H3>Person Details</H3>
      <Paragraph fontSize={1} mb={3}>
        Please select the type of application you would like to create.
      </Paragraph>

      <Paragraph mb={1}>Name*</Paragraph>
      <Input
        placeholder="Enter name"
        value={personSearch}
        onChange={(e) => setPersonSearch(e.currentTarget.value)}
        mb={3}
      />

      <Paragraph mb={1}>Birth Date</Paragraph>
      <DatePicker
        value={dateOfBirthSearch as any}
        onChange={(value: any) => setDateOfBirthSearch(value)}
      />

      <Flex justifyContent="flex-end" mt={2}>
        <Button onClick={handleSearch}>Search</Button>
      </Flex>
    </Box>
  );

  const renderSelectOrCreate = () => (
    <>
      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" minHeight="25vh">
          <Loader />
        </Flex>
      ) : (
        <Box mb={3} minWidth={650}>
          <H3>Select Person or Add New</H3>
          <Paragraph fontSize={1} mb={3}>
            Please select a person from the list.
          </Paragraph>

          <Card
            mb={2}
            p={3}
            style={{ cursor: "pointer" }}
            onClick={() => setMode("addNew")}
          >
            <Paragraph fontWeight={500}>Add new person</Paragraph>
          </Card>

          {results.length === 0 && (
            <Paragraph mt={3} color="gray.40">
              No matching person found. You can add a new person above.
            </Paragraph>
          )}

          {results.map((person: any) => {
            const isSelected = selectedIndividualId === person.id;

            const displayName =
              person.name ||
              [person.first_name, person.middle_name, person.last_name]
                .filter(Boolean)
                .join(" ") ||
              "-";

            const fullAddress = person.address
              ? [
                  person.address.flat_appartment_subbuilding,
                  person.address.building_number,
                  person.address.building_name,
                  person.address.road_street,
                  person.address.town_city,
                  person.address.district,
                  person.address.post_zip_code,
                ]
                  .filter(Boolean)
                  .join(", ")
              : "-";

            const country = person.countryData || null;
            const countryLabel = country?.name;
            const countryFlag = country?.icon;

            const gender = person.gender ? person.gender : null;

            const dob = person.date_of_birth
              ? DateTime.fromISO(person.date_of_birth).toFormat("dd/MM/yyyy")
              : null;

            return (
              <Card
                key={person.id}
                mb={2}
                p={3}
                style={{
                  cursor: "pointer",
                  border: isSelected
                    ? `1px solid ${theme.colors.blue.primary}`
                    : "1px solid transparent",
                  boxShadow: isSelected
                    ? "0 0 0 1px rgba(73, 133, 255, 0.2)"
                    : "none",
                }}
                onClick={() => setSelectedIndividualId(person.id)}
              >
                <Flex justifyContent="space-between" alignItems="center">
                  <Box>
                    <Paragraph fontWeight={600} mb={1}>
                      {displayName}
                    </Paragraph>

                    <Paragraph fontSize={1} color="gray.60">
                      {fullAddress}
                    </Paragraph>

                    {countryLabel && countryFlag ? (
                      <Flex gap="6" alignItems="center" mt={1}>
                        <img
                          src={`data:image/png;base64,${countryFlag}`}
                          alt="flag"
                          width={18}
                        />
                        <Paragraph fontSize={1} color="gray.60" my={0}>
                          {countryLabel}
                        </Paragraph>
                      </Flex>
                    ) : (
                      <Paragraph fontSize={1} color="gray.60" mt={1}>
                        -
                      </Paragraph>
                    )}

                    <Paragraph fontSize={1} color="gray.60" mt={1}>
                      {gender ?? "-"} {dob && <> • {dob}</>}
                    </Paragraph>
                  </Box>

                  <Paragraph
                    fontSize={1}
                    color={theme.colors.blue.primary}
                    style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    View
                  </Paragraph>
                </Flex>
              </Card>
            );
          })}

          <Flex mt={3} justifyContent="space-between">
            <Button variant="text" onClick={() => setMode("search")}>
              Back
            </Button>
            <Button
              onClick={handleNextWithExisting}
              isDisabled={!selectedIndividualId}
            >
              Next
            </Button>
          </Flex>
        </Box>
      )}
    </>
  );

  const renderAddNewIndividual = () => {
    const initialValues: IndividualFormState = {
      division: form.division || singleDivisionId,
      team: form.team || singleTeamId,
      title: form.title || null,
      gender: form.gender || "",
      firstName: form.firstName || "",
      middleName: form.middleName || "",
      lastName: form.lastName || "",
      dateOfBirth: form.dateOfBirth || null,
      email: form.email || "",
      yourReference: form.yourReference || "",
    };

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={getAddIndividualValidation({ requireEmail: false })}
        onSubmit={(values) => {
          handleNextWithNewIndividual(values);
        }}
        enableReinitialize
      >
        {(props) => {
          const isNextDisabled = !props.dirty || !props.isValid;

          return (
            <Form>
              <Box mb={3} minWidth={650}>
                <H3>Add New Person</H3>
                <Paragraph fontSize={1} mb={3}>
                  Please select the type of application you would like to
                  create.
                </Paragraph>

                <Box mb={3}>
                  <Input
                    placeholder="Your Reference"
                    name="yourReference"
                    value={props.values.yourReference}
                    onChange={props.handleChange}
                    label="Your reference"
                    maxLength={50}
                    hasError={Boolean(props.errors.yourReference)}
                    errorMessage={props.errors.yourReference as string}
                  />
                </Box>

                {showDivisionField && (
                  <Box mb={3}>
                    <Select
                      flex={1}
                      onChange={(option: any) => {
                        const newDivisionTeams = teamOptionsFor(option.value);
                        props.setValues({
                          ...props.values,
                          division: option.value,
                          team:
                            newDivisionTeams.length === 1
                              ? newDivisionTeams[0].value
                              : "",
                        });
                      }}
                      value={divisionOptions?.find(
                        ({ value }: any) => value === props.values.division
                      )}
                      options={divisionOptions}
                      placeholder="Choose division"
                      label="Division"
                      name="division"
                      isRequired
                      isDisabled={!hasPermission("profile.add_division")}
                      title={
                        hasPermission("profile.add_division")
                          ? undefined
                          : "You don't have permission to choose a division"
                      }
                      hasError={Boolean(props.errors.division)}
                      errorMessage={props.errors.division as string}
                    />
                  </Box>
                )}
                {teamOptionsFor(props.values.division).length > 0 && (
                  <Box mb={3}>
                    <Select
                      flex={1}
                      key={props.values.division}
                      onChange={(option: any) =>
                        props.setFieldValue("team", option.value)
                      }
                      value={
                        teamOptionsFor(props.values.division).find(
                          ({ value }: any) => value === props.values.team
                        ) ?? null
                      }
                      options={teamOptionsFor(props.values.division)}
                      placeholder="Choose team"
                      label="Team"
                      name="team"
                      isRequired
                      isDisabled={!hasPermission("profile.add_team")}
                      title={
                        hasPermission("profile.add_team")
                          ? undefined
                          : "You don't have permission to choose a team"
                      }
                      hasError={Boolean(props.errors.team)}
                      errorMessage={props.errors.team as string}
                    />
                  </Box>
                )}

                <Flex gap="16" mb={3} flexWrap="wrap">
                  <Box flex={1} minWidth={200}>
                    <Select
                      flex={1}
                      options={titleOptions}
                      placeholder="Select title"
                      value={titleOptions.find(
                        (o) => o.value === props.values.title
                      )}
                      onChange={(option: any) =>
                        props.setFieldValue("title", option.value)
                      }
                      name="title"
                      label="Title"
                      hasError={Boolean(props.errors.title)}
                      errorMessage={props.errors.title as string}
                    />
                  </Box>

                  <Box flex={1} minWidth={200}>
                    <Select
                      flex={1}
                      options={genderOptions}
                      placeholder="Select gender"
                      value={genderOptions.find(
                        (o) => o.value === props.values.gender
                      )}
                      onChange={(option: any) =>
                        props.setFieldValue("gender", option.value)
                      }
                      name="gender"
                      isRequired
                      label="Gender"
                      hasError={Boolean(props.errors.gender)}
                      errorMessage={props.errors.gender as string}
                    />
                  </Box>
                </Flex>

                <Flex gap="16" mb={3} flexWrap="wrap">
                  <Box flex={1} minWidth={200}>
                    <Input
                      placeholder="Enter first name"
                      name="firstName"
                      value={props.values.firstName}
                      onChange={props.handleChange}
                      hasError={Boolean(props.errors.firstName)}
                      errorMessage={props.errors.firstName as string}
                      isRequired
                      label="First name"
                    />
                  </Box>

                  <Box flex={1} minWidth={200}>
                    <Input
                      placeholder="Enter middle name(s)"
                      name="middleName"
                      value={props.values.middleName}
                      onChange={props.handleChange}
                      hasError={Boolean(props.errors.middleName)}
                      errorMessage={props.errors.middleName as string}
                      label="Middle name(s)"
                    />
                  </Box>
                </Flex>

                <Flex gap="16" mb={3} flexWrap="wrap">
                  <Box flex={1} minWidth={200}>
                    <Input
                      placeholder="Enter last name"
                      name="lastName"
                      value={props.values.lastName}
                      onChange={props.handleChange}
                      hasError={Boolean(props.errors.lastName)}
                      errorMessage={props.errors.lastName as string}
                      isRequired
                      label="Last name"
                    />
                  </Box>

                  <Box flex={1} minWidth={200}>
                    <DatePicker
                      label="Date of birth"
                      value={props.values.dateOfBirth as any}
                      onChange={(date: any) =>
                        props.setFieldValue("dateOfBirth", date)
                      }
                      maxDate={DateTime.now()
                        .minus({ years: MIN_AGE_APPLICATION })
                        .toJSDate()}
                      name="dateOfBirth"
                      hasError={Boolean(props.errors.dateOfBirth)}
                      errorMessage={props.errors.dateOfBirth as string}
                    />
                  </Box>
                </Flex>

                <Box mb={3}>
                  <Input
                    placeholder="Enter email address"
                    name="email"
                    value={props.values.email}
                    onChange={props.handleChange}
                    hasError={Boolean(props.errors.email)}
                    label="Email address"
                    errorMessage={props.errors.email as string}
                  />
                </Box>

                <Flex mt={3} justifyContent="space-between">
                  <Button
                    variant="text"
                    type="button"
                    onClick={() => setMode("selectOrCreate")}
                  >
                    Back
                  </Button>
                  <Button type="submit" isDisabled={isNextDisabled}>
                    Next
                  </Button>
                </Flex>
              </Box>
            </Form>
          );
        }}
      </Formik>
    );
  };

  return (
    <NewProfileFrame mode="person" step="personDetails">
      {mode === "search" && renderSearch()}
      {mode === "selectOrCreate" && renderSelectOrCreate()}
      {mode === "addNew" && renderAddNewIndividual()}
    </NewProfileFrame>
  );
};
